import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaTrophy, FaCalendarAlt, FaMapMarkerAlt, FaArrowLeft, FaCheckCircle, FaBrain, FaClock, FaUsers, FaChartLine, FaAward, FaHome } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';

function FestivalsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'active', 'completed'
  const { isDarkMode } = useTheme();

  const festivals = [
    {
      id: 'shomal_ai_2025',
      title: 'SHOMAL AI Connect 2025',
      subtitle: 'همایش تخصصی هوش مصنوعی در دانشگاه شمال',
      description: 'ارتباط، نوآوری و آینده‌سازی با هوش مصنوعی',
      date: 'شنبه، ۶ دی ۱۴۰۴',
      time: '۹:۳۰ تا ۱۸:۰۰',
      location: 'سالن همایش‌های بین‌المللی آمارد - دانشگاه شمال',
      status: 'active',
      poster: 'https://customer-assets.emergentagent.com/job_906637cf-e765-4c1b-8807-5e812e951cd7/artifacts/e6tvuvgm_2025-12-06%20%20%20Shomal%20Ai%20Connect%20Poster%20V05_page-0001.jpg',
      route: '/shomal-ai',
      features: [
        'سخنرانی تخصصی درباره آینده هوش مصنوعی',
        'معرفی کاربرد AI در صنعت و دانشگاه',
        'پنل گفت‌وگو با متخصصان هوش مصنوعی',
        'شبکه‌سازی با فعالان حوزه فناوری'
      ],
      theme: {
        // Light mode: آبی اصلی سایت - Dark mode: آبی روشن‌تر
        gradient: 'from-primary-500 via-primary-600 to-primary-700',
        cardBg: 'bg-white dark:bg-gray-800',
        badgeBg: 'from-accent-500 to-accent-600', // سبز رسمی
        iconColor: 'text-primary-600 dark:text-primary-400',
        accentColor: 'primary',
        borderColor: 'border-primary-200 dark:border-primary-700',
        textGradient: 'from-primary-700 to-primary-500 dark:from-primary-400 dark:to-primary-300'
      }
    },
    {
      id: 'omid',
      title: 'جشنواره امید',
      subtitle: 'جشنواره نوآوری و فناوری',
      description: 'تبدیل ایده‌های نوآورانه به هسته‌های فناور موفق',
      date: '۲۱ آبان تا ۱۲ آذر ۱۴۰۳',
      location: 'سالن همایش بین‌المللی و مرکز رشد و کارآفرینی بین‌المللی دانشگاه شمال',
      status: 'completed',
      poster: 'https://customer-assets.emergentagent.com/job_omid-festival/artifacts/stb9it9r_IMG-20251109-WA0005.jpg',
      route: '/festival',
      stats: {
        registrations: '248+',
        techCores: '200+',
        participants: '250+'
      },
      features: [
        'دریافت مشاوره تخصصی رایگان',
        'امکان تبدیل به هسته فناور',
        'ارتباط با سرمایه‌گذاران و متخصصان',
        'شتاب‌دهی و حمایت از طرح‌ها'
      ],
      theme: {
        // Light mode: طلایی رسمی - Dark mode: طلایی روشن‌تر
        gradient: 'from-secondary-500 via-secondary-600 to-secondary-700',
        cardBg: 'bg-white dark:bg-gray-800',
        badgeBg: 'from-gray-500 to-gray-600',
        iconColor: 'text-secondary-600 dark:text-secondary-400',
        accentColor: 'secondary',
        borderColor: 'border-secondary-200 dark:border-secondary-700',
        textGradient: 'from-secondary-700 to-secondary-500 dark:from-secondary-400 dark:to-secondary-300'
      }
    }
  ];

  // Filter festivals based on active tab
  const filteredFestivals = festivals.filter(festival => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return festival.status === 'active';
    if (activeTab === 'completed') return festival.status === 'completed';
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 py-8 md:py-20">
      {/* SEO */}
      <Helmet>
        <title>جشنواره‌های نوآفرین‌شو | مرکز رشد دانشگاه شمال</title>
        <meta name="description" content="لیست جشنواره‌ها و رویدادهای نوآوری و فناوری مرکز رشد دانشگاه شمال" />
      </Helmet>

      <div className="container mx-auto px-3 sm:px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 md:mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4 md:mb-6">
            <motion.div
              animate={{ rotate: [0, 10, -10, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <FaTrophy className="text-4xl md:text-5xl lg:text-6xl text-secondary-500 dark:text-secondary-400" />
            </motion.div>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-primary-700 via-primary-600 to-primary-500 dark:from-primary-400 dark:via-primary-300 dark:to-primary-400 bg-clip-text text-transparent leading-tight">
            جشنواره‌های نوآفرین‌شو
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed mb-6 md:mb-8 px-2">
            فرصتی برای تبدیل ایده‌های نوآورانه به هسته‌های فناور موفق
          </p>

          {/* Filter Tabs - Improved Mobile */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 md:mb-8 px-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab('all')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 ${
                activeTab === 'all'
                  ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-500/30'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-gray-700 shadow-md border border-gray-200 dark:border-gray-700'
              }`}
            >
              همه
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab('active')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 flex items-center gap-2 ${
                activeTab === 'active'
                  ? 'bg-gradient-to-r from-accent-500 to-accent-600 text-white shadow-lg shadow-accent-500/30'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-accent-50 dark:hover:bg-gray-700 shadow-md border border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${activeTab === 'active' ? 'bg-white animate-pulse' : 'bg-accent-500'}`}></div>
              فعال
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab('completed')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 ${
                activeTab === 'completed'
                  ? 'bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-lg shadow-gray-500/30'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-md border border-gray-200 dark:border-gray-700'
              }`}
            >
              پایان‌یافته
            </motion.button>
          </div>
        </motion.div>

        {/* Festivals Grid with Animation */}
        <AnimatePresence mode="wait">
          <motion.div 
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="max-w-7xl mx-auto space-y-6 md:space-y-8"
          >
            {filteredFestivals.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12 md:py-16"
              >
                <FaTrophy className="text-5xl md:text-6xl text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400">جشنواره‌ای در این دسته یافت نشد</p>
              </motion.div>
            ) : (
              filteredFestivals.map((festival, index) => (
                <motion.div
                  key={festival.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="relative group"
                >
                  {/* Glow Effect - Subtle */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${festival.theme.gradient} opacity-0 group-hover:opacity-10 dark:group-hover:opacity-20 blur-2xl rounded-3xl transition-opacity duration-500`}></div>
                  
                  {/* Main Card - Light/Dark Theme Aware */}
                  <div className={`relative ${festival.theme.cardBg} rounded-2xl md:rounded-3xl shadow-xl hover:shadow-2xl overflow-hidden border-2 ${festival.theme.borderColor} transition-all duration-300`}>
                    
                    {/* Mobile Layout: Stack vertically */}
                    <div className="flex flex-col lg:grid lg:grid-cols-12 gap-4 md:gap-6 p-4 sm:p-5 md:p-8">
                      
                      {/* Poster Section - Full width on mobile */}
                      <div className="lg:col-span-5">
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.3 }}
                          className="relative overflow-hidden rounded-xl md:rounded-2xl shadow-lg h-[200px] sm:h-[250px] md:h-[300px] lg:min-h-[380px]"
                        >
                          <img 
                            src={festival.poster}
                            alt={`پوستر ${festival.title}`}
                            className="w-full h-full object-cover"
                          />
                          
                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                          
                          {/* Status Badge */}
                          <div className={`absolute top-3 right-3 md:top-4 md:right-4 bg-gradient-to-r ${
                            festival.status === 'active' ? festival.theme.badgeBg : festival.theme.badgeBg
                          } text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl font-bold shadow-lg text-xs md:text-sm backdrop-blur-sm ${
                            festival.status === 'active' ? 'animate-pulse' : ''
                          }`}>
                            <FaTrophy className="inline ml-1.5 md:ml-2" />
                            {festival.status === 'active' ? 'ثبت‌نام فعال' : 'پایان یافته'}
                          </div>

                          {/* Quick Stats Overlay - Mobile */}
                          {festival.stats && (
                            <div className="absolute bottom-3 left-3 right-3 md:bottom-4 md:left-4 md:right-4">
                              <div className="bg-black/60 backdrop-blur-md rounded-lg md:rounded-xl p-2 md:p-3">
                                <div className="grid grid-cols-3 gap-1 md:gap-2 text-center">
                                  <div>
                                    <div className="text-base md:text-xl font-bold text-white">{festival.stats.registrations}</div>
                                    <div className="text-[10px] md:text-xs text-gray-300">طرح</div>
                                  </div>
                                  <div>
                                    <div className="text-base md:text-xl font-bold text-white">{festival.stats.techCores}</div>
                                    <div className="text-[10px] md:text-xs text-gray-300">هسته</div>
                                  </div>
                                  <div>
                                    <div className="text-base md:text-xl font-bold text-white">{festival.stats.participants}</div>
                                    <div className="text-[10px] md:text-xs text-gray-300">شرکت‌کننده</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      </div>

                      {/* Details Section */}
                      <div className="lg:col-span-7 flex flex-col justify-between">
                        {/* Title & Description */}
                        <div className="mb-4 md:mb-6">
                          <h2 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3 flex items-center gap-2 md:gap-3 bg-gradient-to-r ${festival.theme.textGradient} bg-clip-text text-transparent`}>
                            {festival.id === 'shomal_ai_2025' ? (
                              <FaBrain className={`${festival.theme.iconColor} text-xl md:text-2xl flex-shrink-0`} />
                            ) : (
                              <FaTrophy className={`${festival.theme.iconColor} text-xl md:text-2xl flex-shrink-0`} />
                            )}
                            <span className="leading-tight">{festival.title}</span>
                          </h2>
                          
                          <p className="text-sm md:text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2 md:mb-3">
                            {festival.subtitle}
                          </p>
                          
                          <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                            {festival.description}
                          </p>
                        </div>

                        {/* Event Info Cards - Improved Mobile Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
                          <div className="bg-primary-50 dark:bg-gray-700/50 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-primary-100 dark:border-gray-600">
                            <div className="flex items-start gap-2 md:gap-3">
                              <FaCalendarAlt className={`${festival.theme.iconColor} text-base md:text-xl mt-0.5 flex-shrink-0`} />
                              <div>
                                <p className="text-[11px] md:text-sm text-gray-500 dark:text-gray-400 mb-0.5 md:mb-1">تاریخ برگزاری</p>
                                <p className="font-bold text-gray-800 dark:text-gray-200 text-sm md:text-base">{festival.date}</p>
                                {festival.time && (
                                  <p className="text-[11px] md:text-sm text-gray-500 dark:text-gray-400 mt-0.5 md:mt-1 flex items-center gap-1.5">
                                    <FaClock className="text-[10px] md:text-xs" />
                                    {festival.time}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-secondary-50 dark:bg-gray-700/50 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-secondary-100 dark:border-gray-600">
                            <div className="flex items-start gap-2 md:gap-3">
                              <FaMapMarkerAlt className={`${festival.theme.iconColor} text-base md:text-xl mt-0.5 flex-shrink-0`} />
                              <div>
                                <p className="text-[11px] md:text-sm text-gray-500 dark:text-gray-400 mb-0.5 md:mb-1">مکان برگزاری</p>
                                <p className="font-bold text-gray-800 dark:text-gray-200 leading-snug text-sm md:text-base">{festival.location}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Features or Achievement Stats */}
                        {festival.status === 'active' ? (
                          <div className="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-gray-700/50 dark:to-gray-600/50 backdrop-blur-sm rounded-xl p-4 md:p-5 border border-primary-100 dark:border-gray-600 mb-4 md:mb-6">
                            <h4 className="text-sm md:text-lg font-bold text-gray-800 dark:text-gray-200 mb-2 md:mb-3 flex items-center gap-2">
                              <FaAward className={festival.theme.iconColor} />
                              ویژگی‌های ویژه
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                              {festival.features.map((feature, idx) => (
                                <div key={idx} className="flex items-start gap-2">
                                  <FaCheckCircle className={`${festival.theme.iconColor} text-xs md:text-sm mt-0.5 flex-shrink-0`} />
                                  <span className="text-xs md:text-sm text-gray-600 dark:text-gray-300">{feature}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          festival.stats && (
                            <div className="bg-gradient-to-br from-secondary-50 to-gray-50 dark:from-gray-700/50 dark:to-gray-600/50 backdrop-blur-sm rounded-xl p-4 md:p-5 border border-secondary-100 dark:border-gray-600 mb-4 md:mb-6">
                              <h4 className="text-sm md:text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 md:mb-4 flex items-center gap-2">
                                <FaChartLine className={festival.theme.iconColor} />
                                آمار و دستاوردها
                              </h4>
                              <div className="grid grid-cols-3 gap-2 md:gap-4">
                                <div className="text-center p-2 md:p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                  <FaUsers className={`${festival.theme.iconColor} text-lg md:text-2xl mx-auto mb-1 md:mb-2`} />
                                  <div className="text-lg md:text-2xl font-bold text-gray-800 dark:text-gray-200 mb-0.5">{festival.stats.registrations}</div>
                                  <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400">طرح ثبت‌شده</div>
                                </div>
                                <div className="text-center p-2 md:p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                  <FaAward className={`${festival.theme.iconColor} text-lg md:text-2xl mx-auto mb-1 md:mb-2`} />
                                  <div className="text-lg md:text-2xl font-bold text-gray-800 dark:text-gray-200 mb-0.5">{festival.stats.techCores}</div>
                                  <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400">هسته فناور</div>
                                </div>
                                <div className="text-center p-2 md:p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                  <FaChartLine className={`${festival.theme.iconColor} text-lg md:text-2xl mx-auto mb-1 md:mb-2`} />
                                  <div className="text-lg md:text-2xl font-bold text-gray-800 dark:text-gray-200 mb-0.5">{festival.stats.participants}</div>
                                  <div className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400">مشارکت‌کننده</div>
                                </div>
                              </div>
                            </div>
                          )
                        )}

                        {/* CTA Button - Improved Mobile */}
                        <motion.button
                          onClick={() => navigate(festival.route)}
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          className={`w-full inline-flex items-center justify-center gap-2 md:gap-3 px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r ${
                            festival.status === 'active'
                              ? 'from-primary-600 via-primary-700 to-primary-600 hover:from-primary-700 hover:via-primary-800 hover:to-primary-700'
                              : 'from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
                          } text-white rounded-xl md:rounded-2xl font-bold text-sm md:text-lg shadow-lg hover:shadow-xl transition-all`}
                        >
                          {festival.status === 'active' ? 'ثبت‌نام در همایش' : 'مشاهده جزئیات و گالری'}
                          <FaArrowLeft className="text-sm md:text-lg" />
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>
        </AnimatePresence>

        {/* Back to Home - Improved Mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-8 md:mt-12"
        >
          <motion.button
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 md:gap-3 px-6 md:px-8 py-3 md:py-4 bg-white dark:bg-gray-800 border-2 border-primary-500 dark:border-primary-400 text-primary-700 dark:text-primary-400 rounded-xl md:rounded-2xl font-bold text-sm md:text-lg shadow-lg hover:shadow-xl hover:bg-primary-50 dark:hover:bg-gray-700 transition-all"
          >
            <FaHome className="text-lg md:text-xl" />
            بازگشت به صفحه اصلی
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
}

export default FestivalsPage;